import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface SubscriptionStatus {
  subscribed: boolean;
  plan: string;
  plan_status: 'trial' | 'pro';
  subscription_end: string | null;
  trial_days_used: number;
  trial_expired: boolean;
}

const DEFAULT_SUBSCRIPTION_STATUS: SubscriptionStatus = {
  subscribed: false,
  plan: 'trial',
  plan_status: 'trial',
  subscription_end: null,
  trial_days_used: 0,
  trial_expired: false,
};

export function useSubscription() {
  const { user, session } = useAuth();

  return useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async (): Promise<SubscriptionStatus> => {
      if (!session?.access_token) {
        console.log('[useSubscription] No session, returning default');
        return DEFAULT_SUBSCRIPTION_STATUS;
      }

      try {
        const { data, error } = await supabase.functions.invoke('check-subscription', {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (error) {
          // Log detailed error info for debugging
          console.warn('[useSubscription] Edge Function error:', {
            name: error.name,
            message: error.message,
            context: error.context,
          });
          // Return safe fallback - don't block the app
          return DEFAULT_SUBSCRIPTION_STATUS;
        }

        return data as SubscriptionStatus;
      } catch (err) {
        // Network errors, timeouts, etc.
        const error = err as Error;
        console.warn('[useSubscription] Network/fetch error:', {
          name: error.name,
          message: error.message,
        });
        // Return safe fallback - don't block the app
        return DEFAULT_SUBSCRIPTION_STATUS;
      }
    },
    enabled: !!user?.id && !!session?.access_token,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // Refresh every minute
    retry: 2, // Retry twice on failure
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
    // Don't throw errors to the UI - we handle them gracefully
    throwOnError: false,
  });
}

export function useCreateCheckout() {
  const { session } = useAuth();

  return useMutation({
    mutationFn: async (plan: 'mensal' | 'anual' | 'teste') => {
      if (!session?.access_token) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      return data as { url: string };
    },
  });
}

export function useCustomerPortal() {
  const { session } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!session?.access_token) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      return data as { url: string };
    },
  });
}

export function useRefreshSubscription() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      await queryClient.invalidateQueries({ queryKey: ['subscription', user?.id] });
      await queryClient.invalidateQueries({ queryKey: ['trial-status', user?.id] });
      await queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
    },
  });
}
